import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { routePaths } from '../../routes/routes-path';
import { getMyPublisher } from '../../api/publisher/publisher.api';
import { getAffiliatePartnerStatus } from '../../api/affiliate/affiliate.api';

/**
 * Hub "Hợp tác cùng Cab" — gộp Publisher (web nhúng quảng cáo) + Affiliate (giới thiệu sản phẩm ăn hoa hồng).
 * Giao diện bê nguyên landing đẹp của public/publisher-invite.html (dark gradient, Plus Jakarta) — BỎ header/nav
 * riêng vì app đã có navbar/sidebar. Chức năng thật: CTA dẫn vào /publisher & /affiliate (react-router),
 * calculator + copy ad-tag chạy được. Số liệu ĐÚNG hệ thống thật (68% / giữ 30 ngày / min 100k / thuế TT111/2013).
 * Theo toggle sáng/tối của app (Tailwind darkMode:'class' → html.dark): sáng = nền trắng, tối = nền đen.
 * Accent xanh brand Cab (#0284c7); bản sáng dùng sắc xanh đậm hơn cho dễ đọc. Khối code (.term) luôn tối.
 */

const Check = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const fmt = (n: number) => Math.round(n).toLocaleString('vi-VN');

const PartnerPage: React.FC = () => {
  const [pv, setPv] = useState(10000);
  const [copied, setCopied] = useState(false);
  // Trạng thái publisher + affiliate của user → chỉ hiện chương trình đã tham gia; chưa gì thì mới mời chọn.
  const [pubStatus, setPubStatus] = useState<string | null>(null);
  const [affStatus, setAffStatus] = useState<string | null>(null);
  useEffect(() => {
    getMyPublisher().then((p) => setPubStatus(p?.status || null)).catch(() => undefined);
    getAffiliatePartnerStatus().then((p) => setAffStatus(p?.status || null)).catch(() => undefined);
  }, []);
  const joined = (s: string | null) => s === 'Pending' || s === 'Approved';
  const pubJoined = joined(pubStatus);
  const affJoined = joined(affStatus);
  const anyJoined = pubJoined || affJoined;
  const pubCta = pubStatus === 'Approved' ? 'Vào Dashboard Publisher'
    : pubStatus === 'Pending' ? 'Hồ sơ đang chờ duyệt'
      : 'Đăng ký làm Publisher';
  const affCta = affStatus === 'Approved' ? 'Vào Dashboard Affiliate'
    : affStatus === 'Pending' ? 'Hồ sơ đang chờ duyệt'
      : 'Đăng ký làm Affiliate';

  const monthly = pv * 30 * (30000 / 1000) * 0.68; // pv/ngày × 30 × eCPM minh hoạ × 68%
  const snippet =
    '<ins class="cab-ad" data-cab-pub="CAB_PUBLISHER_KEY"></ins>\n' +
    '<script async\n' +
    '  src="https://api.beta.cab.vn/v1/payment-service/public/ads/cab-ads.js"\n' +
    '  data-cab-pub="CAB_PUBLISHER_KEY"></script>';

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="phub">
      <Helmet>
        <title>Hợp tác cùng Cab</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <style>{CSS}</style>

      {/* HERO */}
      <div className="phub-hero">
        <div className="wrap">
          <span className="pill"><span className="ping" /> Nền tảng Affiliate &amp; Ad Network của hệ sinh thái Cab.vn</span>
          <h1>Biến traffic thành <span className="grad">nguồn thu nhập thụ động</span> bền vững</h1>
          <p>
            Dù bạn là chủ website (Publisher) hay nhà tiếp thị liên kết (Affiliate), Cab.vn mang đến cơ chế
            chia sẻ doanh thu hấp dẫn và dòng tiền "Ca" minh bạch — thanh toán đúng luật Việt Nam.
          </p>
          <div className="hero-cta">
            <a className="btn btn-grad" href="#phub-models">Bắt đầu kiếm tiền</a>
            <a className="btn btn-ghost" href="#phub-calc">Dùng thử công cụ tính</a>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="statbar">
        <div className="wrap stats">
          <div><div className="n grad">68%</div><div className="l">Tỷ lệ chia sẻ doanh thu</div></div>
          <div><div className="n" style={{ color: 'var(--indigo-l)' }}>Real-time</div><div className="l">Theo dõi &amp; báo cáo</div></div>
          <div><div className="n" style={{ color: 'var(--purple)' }}>Minh bạch</div><div className="l">Đối soát rõ ràng từng khoản</div></div>
          <div><div className="n" style={{ color: 'var(--pink)' }}>Miễn phí</div><div className="l">Tham gia, không phí ẩn</div></div>
        </div>
      </div>

      {/* FEATURES */}
      <section>
        <div className="wrap">
          <div className="center"><h2>Lợi thế khi đồng hành cùng Cab.vn</h2><p className="lead">Công bằng, minh bạch, đúng luật — thiết kế cho đối tác Việt.</p></div>
          <div className="grid g3">
            <div className="card"><div className="ic">01</div><h3>Tài khoản All-in-One</h3><p>Một tài khoản Cab.vn duy nhất quản lý cả website (Publisher) lẫn link tiếp thị (Affiliate).</p></div>
            <div className="card"><div className="ic">02</div><h3>Tích hợp 1 đoạn mã</h3><p>Dán một đoạn JavaScript là chạy — tải bất đồng bộ, không làm chậm web, không cần tài khoản mới.</p></div>
            <div className="card"><div className="ic">03</div><h3>Thanh toán minh bạch, đúng luật</h3><p>Đối soát theo ngày, giữ 30 ngày chống gian lận, chi trả tối thiểu 100.000đ; khấu trừ thuế theo quy định.</p></div>
          </div>
        </div>
      </section>

      {/* MODELS */}
      <section id="phub-models" className="band">
        <div className="wrap" style={{ maxWidth: 960 }}>
          <div className="center">
            <h2>{anyJoined ? 'Khu vực hợp tác của bạn' : 'Chọn hình thức hợp tác'}</h2>
            <p className="lead">{anyJoined ? 'Vào dashboard để quản lý chương trình bạn đang tham gia.' : 'Phù hợp với mọi định hướng khai thác nội dung & traffic của bạn.'}</p>
          </div>
          <div className="grid g2">
            {/* Publisher — LUÔN hiện thành 1 thẻ (chưa tham gia = trạng thái mời) để cân bằng 2 cột, không rớt thành text. */}
            {(
              <div className="model">
                <div>
                  {pubJoined
                    ? <span className="kicker" style={{ background: 'rgba(2,132,199,.1)', color: 'var(--indigo-l)', border: '1px solid rgba(2,132,199,.2)' }}>{pubStatus === 'Approved' ? '● Đang hoạt động' : 'Đang chờ duyệt'}</span>
                    : <span className="kicker" style={{ background: 'rgba(2,132,199,.1)', color: 'var(--indigo-l)', border: '1px solid rgba(2,132,199,.2)' }}>Dành cho Publisher / Webmaster</span>}
                  <h3>Ad Network</h3>
                  <p>Đặt quảng cáo của Cab lên website để kiếm tiền từ lượng truy cập sẵn có.</p>
                  <ul>
                    <li><Check /><span>Chia sẻ doanh thu <b>68%</b> (chuẩn AdSense)</span></li>
                    <li><Check /><span>Dán 1 đoạn mã, xác minh domain là chạy</span></li>
                    <li><Check /><span>Chống gian lận + an toàn thương hiệu sẵn</span></li>
                  </ul>
                </div>
                <Link className="cta btn-ghost" to={routePaths.publisher}>{pubCta}</Link>
              </div>
            )}
            {/* Affiliate — LUÔN hiện thành 1 thẻ (chưa tham gia = trạng thái mời). */}
            {(
              <div className="model hot">
                {!affJoined && <div className="badge">Phổ biến</div>}
                <div>
                  {affJoined
                    ? <span className="kicker" style={{ background: 'rgba(34,211,238,.1)', color: 'var(--pink)', border: '1px solid rgba(34,211,238,.2)' }}>{affStatus === 'Approved' ? '● Đang hoạt động' : 'Đang chờ duyệt'}</span>
                    : <span className="kicker" style={{ background: 'rgba(34,211,238,.1)', color: 'var(--pink)', border: '1px solid rgba(34,211,238,.2)' }}>Dành cho Affiliate Marketer</span>}
                  <h3>Tiếp thị liên kết</h3>
                  <p>Lấy link sản phẩm rồi chia sẻ. Có ai mua qua link của bạn thì bạn được hoa hồng.</p>
                  <ul>
                    <li><Check /><span>Hoa hồng tính theo từng đơn, có đếm lượt bấm và tỉ lệ mua</span></li>
                    <li><Check /><span>Đăng ký cộng tác viên, duyệt xong là bán được</span></li>
                    <li><Check /><span>Xem thu nhập và biểu đồ theo từng ngày</span></li>
                  </ul>
                </div>
                <Link className="cta btn-grad" to={routePaths.shop}>{affCta}</Link>
              </div>
            )}
          </div>
          {/* (Bỏ dòng text mời mỏng — cả 2 thẻ đã luôn hiện đầy đủ ở trên) */}
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="phub-calc">
        <div className="wrap">
          <div className="center"><h2>Ước tính tiềm năng thu nhập</h2><p className="lead">Kéo thanh trượt theo lượng truy cập website của bạn để hình dung.</p></div>
          <div className="calc">
            <div className="calc-top"><label htmlFor="trafficRange">Lượt xem trang (pageviews) / ngày</label><span id="trafficValue">{fmt(pv)}</span></div>
            <input id="trafficRange" type="range" min={1000} max={200000} step={1000} value={pv} onChange={(e) => setPv(parseInt(e.target.value, 10))} />
            <div className="calc-out">
              <div><span className="k">Thu nhập minh hoạ / tháng</span><span className="v" style={{ color: 'var(--emerald)' }}>~ {fmt(monthly)}đ</span></div>
              <div><span className="k">Tỷ lệ chia cho bạn</span><span className="v" style={{ color: 'var(--indigo-l)' }}>68%</span></div>
            </div>
            <p className="disc">* Chỉ mang tính minh hoạ, KHÔNG phải cam kết. Doanh thu thực tế tuỳ lĩnh vực, chất lượng traffic và lượng quảng cáo khả dụng. Chưa trừ thuế.</p>
          </div>
        </div>
      </section>

      {/* INTEGRATION */}
      <section className="band">
        <div className="wrap">
          <div className="center"><h2>Mã nhúng tích hợp nhanh</h2><p className="lead">Chỉ một đoạn ad-tag là website của bạn đã sẵn sàng kiếm tiền (dành cho Publisher).</p></div>
          <div className="term">
            <div className="term-bar">
              <div className="dots"><i style={{ background: '#ef4444' }} /><i style={{ background: '#eab308' }} /><i style={{ background: '#22c55e' }} /><span className="fname">cab-ads-embed.html</span></div>
              <button className="copy" onClick={copy} style={copied ? { background: '#059669' } : undefined}>{copied ? 'Đã sao chép!' : 'Sao chép mã'}</button>
            </div>
            <pre><code>{snippet}</code></pre>
          </div>
          <p className="disc">Thay <code>CAB_PUBLISHER_KEY</code> bằng khoá riêng nhận được sau khi đăng ký &amp; xác minh domain.</p>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="wrap" style={{ maxWidth: 780 }}>
          <div className="center"><h2>Câu hỏi thường gặp</h2><p className="lead">Giải đáp riêng cho Publisher và Affiliate Partner.</p></div>

          <p className="faqcat" style={{ color: 'var(--indigo-l)' }}>Dành cho Publisher (Chủ website)</p>
          <details><summary>Điều kiện để website được tham gia Ad Network?</summary><p>Website hoạt động ổn định, nội dung chính thống (không vi phạm pháp luật, không mã độc/cờ bạc/đồi trụy) và bạn là chủ sở hữu domain (cần xác minh). Quảng cáo chỉ chạy sau khi xác minh sở hữu domain.</p></details>
          <details><summary>Gắn mã có làm chậm website không?</summary><p>Không. Đoạn mã tải bất đồng bộ (async) nên không chặn tải trang, không ảnh hưởng tốc độ và SEO.</p></details>
          <details><summary>Publisher được chia bao nhiêu và thanh toán thế nào?</summary><p>Chia 68% doanh thu trên mỗi lượt hợp lệ. Doanh thu giữ 30 ngày rà soát gian lận, sau đó Cab đối soát &amp; chi trả khi đạt tối thiểu 100.000đ. Cá nhân khấu trừ thuế TNCN 10% với mỗi lần chi từ 5 triệu (NĐ 253/2026); doanh nghiệp/hộ KD xuất hóa đơn điện tử.</p></details>

          <p className="faqcat" style={{ color: 'var(--pink)', marginTop: 28 }}>Dành cho Affiliate Marketer</p>
          <details><summary>Làm thế nào để lấy link tiếp thị?</summary><p>Bạn đăng ký làm cộng tác viên, duyệt xong thì vào mục "Tiếp thị liên kết", chọn sản phẩm và lấy link. Link này tự đếm lượt bấm cho bạn.</p></details>
          <details><summary>Đối soát &amp; thanh toán hoa hồng khi nào?</summary><p>Mỗi đơn mua qua link của bạn đều được ghi hoa hồng. Cab giữ lại 30 ngày phòng khi khách đổi hoặc trả hàng, hết 30 ngày thì số đó thành Ca và bạn rút được. Bạn xem được ngay trong mục thu nhập.</p></details>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="wrap">
          <div className="ctaband">
            <h2>Sẵn sàng bắt đầu kiếm tiền cùng Cab.vn?</h2>
            <p>Dùng ngay tài khoản Cab.vn hiện có để đăng ký đối tác — vài phút là bắt đầu.</p>
            <a className="btn btn-dark" href="#phub-models">Chọn hình thức hợp tác</a>
            <div className="note">Hoặc liên hệ: ceo@cab.vn · Hotline 0988 779 950</div>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap foot">
          <div>© 2026 CÔNG TY TNHH WCAB NETWORK · Cab Partner Hub</div>
          <div><a href="/operating_regulations_and_term_of_service.html" style={{ color: 'var(--muted)' }}>Điều khoản &amp; Chính sách</a></div>
        </div>
      </footer>
    </div>
  );
};

/* CSS bê từ publisher-invite.html, scope hết dưới .phub để không đụng global app. Bỏ header/nav. */
const CSS = `
/* Theo toggle sáng/tối của app (Tailwind darkMode:'class' → html.dark). Mặc định SÁNG; .dark .phub = TỐI.
   Accent brand xanh Cab; bản sáng dùng sắc xanh đậm hơn để đọc rõ trên nền trắng, bản tối dùng sắc tươi. */
.phub { --bg:#ffffff; --bg2:#f8fafc; --bg3:#e2e8f0; --line:#e5e9f0; --line2:#cbd5e1; --ink:#0f172a; --muted:#64748b;
  --card-bg:#ffffff; --band-bg:#f8fafc; --inset-bg:#f1f5f9;
  --indigo:#0284c7; --indigo-l:#0369a1; --purple:#0284c7; --pink:#0891b2; --emerald:#059669;
  font-family:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; background:var(--bg); color:var(--ink); line-height:1.6; -webkit-font-smoothing:antialiased; border-radius:16px; overflow:hidden; border:1px solid var(--line); }
.dark .phub { --bg:#020617; --bg2:#0f172a; --bg3:#1e293b; --line:#1e293b; --line2:#334155; --ink:#e2e8f0; --muted:#94a3b8;
  --card-bg:rgba(15,23,42,.6); --band-bg:rgba(15,23,42,.3); --inset-bg:rgba(2,6,23,.6);
  --indigo-l:#38bdf8; --purple:#0ea5e9; --pink:#22d3ee; --emerald:#34d399; }
.phub *{ box-sizing:border-box; }
.phub h1,.phub h2,.phub h3,.phub h4{ color:var(--ink)!important; }
.phub .ctaband h2{ color:#fff!important; }
.phub a{ text-decoration:none; }
.phub .wrap{ max-width:1120px; margin:0 auto; padding:0 20px; }
.phub .grad{ background:linear-gradient(90deg,var(--indigo-l),var(--purple),var(--pink)); -webkit-background-clip:text; background-clip:text; color:transparent; }
.phub .btn{ display:inline-block; font-weight:700; font-size:14px; padding:11px 20px; border-radius:12px; transition:filter .15s, transform .05s; cursor:pointer; }
.phub .btn:active{ transform:translateY(1px); }
.phub .btn-grad{ background:linear-gradient(90deg,var(--indigo),var(--purple)); color:#fff; box-shadow:0 10px 26px -8px rgba(2,132,199,.5); }
.phub .btn-grad:hover{ filter:brightness(1.1); }
.phub .btn-ghost{ background:var(--bg2); color:var(--ink); border:1px solid var(--line2); }
.phub .phub-hero{ position:relative; overflow:hidden; padding:76px 0 92px; text-align:center; }
.phub .phub-hero::before{ content:''; position:absolute; inset:0; background:radial-gradient(ellipse 60% 50% at 50% 0%, rgba(2,132,199,.18), transparent); }
.phub .pill{ position:relative; display:inline-flex; align-items:center; gap:8px; font-size:12.5px; font-weight:600; color:var(--indigo-l); background:rgba(2,132,199,.1); border:1px solid rgba(2,132,199,.2); border-radius:999px; padding:6px 14px; margin-bottom:24px; }
.phub .ping{ width:8px; height:8px; border-radius:50%; background:var(--indigo-l); animation:phubping 1.4s infinite; }
@keyframes phubping{ 0%{ box-shadow:0 0 0 0 rgba(56,189,248,.6);} 70%{ box-shadow:0 0 0 8px rgba(56,189,248,0);} 100%{ box-shadow:0 0 0 0 rgba(56,189,248,0);} }
.phub .phub-hero h1{ position:relative; font-size:44px; font-weight:800; letter-spacing:-.02em; line-height:1.12; max-width:820px; margin:0 auto; }
.phub .phub-hero p{ position:relative; color:var(--muted); font-size:18px; max-width:660px; margin:22px auto 34px; }
.phub .hero-cta{ position:relative; display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }
@media (max-width:620px){ .phub .phub-hero h1{ font-size:31px; } .phub .phub-hero p{ font-size:16px; } }
.phub .statbar{ border-top:1px solid var(--line); border-bottom:1px solid var(--line); background:var(--band-bg); }
.phub .stats{ display:grid; grid-template-columns:repeat(4,1fr); gap:24px; padding:40px 20px; text-align:center; }
@media (max-width:720px){ .phub .stats{ grid-template-columns:repeat(2,1fr); } }
.phub .stats .n{ font-size:32px; font-weight:800; }
.phub .stats .l{ font-size:13.5px; color:var(--muted); margin-top:4px; }
.phub section{ padding:64px 0; }
.phub .band{ background:var(--band-bg); border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
.phub .center{ text-align:center; max-width:640px; margin:0 auto 44px; }
.phub h2{ font-size:29px; font-weight:800; letter-spacing:-.01em; }
.phub .lead{ color:var(--muted); margin-top:10px; font-size:16px; }
.phub .grid{ display:grid; gap:20px; }
.phub .g3{ grid-template-columns:repeat(3,1fr); }
.phub .g2{ grid-template-columns:repeat(2,1fr); }
@media (max-width:860px){ .phub .g3{ grid-template-columns:1fr; } .phub .g2{ grid-template-columns:1fr; } }
.phub .card{ background:var(--card-bg); border:1px solid var(--line); border-radius:22px; padding:30px; transition:border-color .25s; }
.phub .card:hover{ border-color:rgba(2,132,199,.5); }
.phub .ic{ width:48px; height:48px; border-radius:14px; display:grid; place-items:center; font-weight:800; font-size:18px; margin-bottom:20px; background:rgba(2,132,199,.1); color:var(--indigo-l); border:1px solid rgba(2,132,199,.2); }
.phub .card h3{ font-size:19px; margin-bottom:8px; }
.phub .card p{ color:var(--muted); font-size:14.5px; }
.phub .model{ background:var(--bg2); border:1px solid var(--line); border-radius:24px; padding:32px; display:flex; flex-direction:column; position:relative; overflow:hidden; }
.phub .model.hot{ background:linear-gradient(180deg, rgba(7,89,133,.25), var(--bg2)); border-color:rgba(2,132,199,.3); }
.phub .model .badge{ position:absolute; top:0; right:0; background:linear-gradient(90deg,var(--indigo),var(--purple)); color:#fff; font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; padding:4px 16px; border-bottom-left-radius:12px; }
.phub .model .kicker{ display:inline-block; font-size:12px; font-weight:600; border-radius:999px; padding:4px 12px; margin-bottom:14px; }
.phub .model h3{ font-size:22px; margin-bottom:8px; }
.phub .model > div > p{ color:var(--muted); font-size:14px; margin-bottom:20px; }
.phub .model ul{ list-style:none; margin-bottom:28px; padding:0; }
.phub .model li{ display:flex; gap:8px; font-size:14px; color:var(--ink); padding:6px 0; }
.phub .model li svg{ flex:0 0 18px; color:var(--emerald); margin-top:2px; }
.phub .model .cta{ margin-top:auto; text-align:center; font-weight:700; padding:13px; border-radius:14px; }
.phub .calc{ background:var(--bg2); border:1px solid var(--line); border-radius:24px; padding:32px; max-width:780px; margin:0 auto; }
.phub .calc-top{ display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
.phub .calc-top label{ font-size:14px; font-weight:600; color:var(--muted); }
.phub #trafficValue{ font-size:22px; font-weight:800; color:var(--indigo-l); }
.phub input[type=range]{ width:100%; height:8px; border-radius:8px; background:var(--bg3); -webkit-appearance:none; appearance:none; cursor:pointer; accent-color:var(--indigo); }
.phub .calc-out{ display:grid; grid-template-columns:1fr 1fr; gap:20px; text-align:center; margin-top:26px; background:var(--inset-bg); border:1px solid var(--line); border-radius:18px; padding:24px; }
@media (max-width:560px){ .phub .calc-out{ grid-template-columns:1fr; } }
.phub .calc-out .k{ font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:var(--muted); display:block; margin-bottom:6px; }
.phub .calc-out .v{ font-size:30px; font-weight:800; }
.phub .disc{ text-align:center; color:var(--muted); font-size:12.5px; margin-top:16px; }
.phub .term{ background:#0b1220; border:1px solid var(--line); border-radius:18px; overflow:hidden; max-width:820px; margin:0 auto; }
.phub .term-bar{ background:#111a2e; padding:14px 18px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--line); }
.phub .dots{ display:flex; gap:7px; align-items:center; }
.phub .dots i{ width:12px; height:12px; border-radius:50%; display:inline-block; }
.phub .fname{ color:var(--muted); font-size:12px; margin-left:10px; font-family:ui-monospace,Menlo,Consolas,monospace; }
.phub .copy{ font-size:12px; font-weight:600; background:var(--indigo); color:#fff; border:0; padding:7px 14px; border-radius:9px; cursor:pointer; }
.phub .term pre{ padding:22px; overflow-x:auto; margin:0; }
.phub .term code{ font-family:ui-monospace,Menlo,Consolas,monospace; font-size:13px; color:#7dd3fc; line-height:1.7; white-space:pre; }
.phub .faqcat{ font-size:12px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; margin:0 0 14px 4px; }
.phub details{ background:var(--bg2); border:1px solid var(--line); border-radius:14px; padding:16px 20px; margin-bottom:12px; }
.phub summary{ font-weight:600; cursor:pointer; list-style:none; display:flex; justify-content:space-between; gap:12px; }
.phub summary::-webkit-details-marker{ display:none; }
.phub summary::after{ content:'▾'; color:var(--muted); transition:transform .2s; }
.phub details[open] summary::after{ transform:rotate(180deg); }
.phub details p{ color:var(--muted); font-size:14.5px; margin-top:10px; }
.phub .ctaband{ background:linear-gradient(90deg,var(--indigo),var(--purple),var(--pink)); border-radius:24px; padding:52px 32px; text-align:center; }
.phub .ctaband h2{ color:#fff; }
.phub .ctaband p{ color:rgba(255,255,255,.9); margin:12px auto 24px; max-width:560px; }
.phub .ctaband .btn-dark{ background:#0f172a; color:#fff!important; }
.phub .ctaband .btn-dark:hover{ background:#1e293b; }
.phub .ctaband .note{ color:rgba(255,255,255,.85); font-size:13px; margin-top:14px; }
.phub footer{ border-top:1px solid var(--line); padding:34px 0; color:var(--muted); font-size:13px; }
.phub .foot{ display:flex; justify-content:space-between; flex-wrap:wrap; gap:12px; }
`;

export default PartnerPage;
